<template>
  <div class="vehicle_data_page">
    <!-- EMPTY STATE CONTAINER (WHEN NO VEHICLE DATA EXISTS) -->
    <div
      class="empty_vehicle_card py-5 px-4 text-center my-4"
      v-if="!hasVehicleData"
    >
      <div class="empty_icon_wrapper mx-auto mb-4">
        <div class="empty_icon_box">
          <i class="fal fa-car-alt"></i>
        </div>
        <div class="pulse_ring"></div>
      </div>
      <h4 class="empty_title font-weight-bold mb-2">
        لا توجد بيانات مركبة مسجلة
      </h4>
      <p
        class="empty_subtitle text-muted mx-auto mb-4"
        style="max-width: 480px"
      >
        لم يقم هذا السائق بإضافة تفاصيل السيارة، الرخصة، أو الوثائق الرسمية
        للمركبة حتى الآن.
      </p>
    </div>

    <!-- VEHICLE DATA DETAILS (WHEN VEHICLE DATA EXISTS) -->
    <div class="vehicle_details_container" v-else>
      <!-- HEADER BANNER / OVERVIEW -->
      <div class="section_title_bar mb-4">
        <div class="title_group">
          <i class="fal fa-car-building icon_main"></i>
          <div>
            <h5 class="m-0 font-weight-bold">بيانات ووثائق المركبة</h5>
            <span class="text-muted text_small"
              >تفاصيل ترخيص وسيارة السائق المسجلة</span
            >
          </div>
        </div>
        <span class="badge_type_pill">
          <i class="fal fa-shield-alt me-1"></i>
          {{ formattedVehicleType }}
        </span>
      </div>

      <!-- SPECIFICATION CARDS GRID -->
      <div class="row g-4 mb-4">
        <!-- VEHICLE NUMBER -->
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <div class="spec_info_card">
            <div class="spec_icon_box icon_cyan">
              <i class="fal fa-car-alt"></i>
            </div>
            <div class="spec_details">
              <span class="spec_label">رقم المركبة / لوحة السيارة</span>
              <h4 class="spec_value">
                {{ vehicle.vehicle_number || "غير مسجل" }}
              </h4>
            </div>
          </div>
        </div>

        <!-- SERIAL NUMBER -->
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <div class="spec_info_card">
            <div class="spec_icon_box icon_purple">
              <i class="fal fa-barcode"></i>
            </div>
            <div class="spec_details">
              <span class="spec_label">الرقم التسلسلي</span>
              <h4 class="spec_value">
                {{ vehicle.vehicle_serial_number || "غير مسجل" }}
              </h4>
            </div>
          </div>
        </div>

        <!-- VEHICLE TYPE -->
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <div class="spec_info_card">
            <div class="spec_icon_box icon_amber">
              <i class="fal fa-steering-wheel"></i>
            </div>
            <div class="spec_details">
              <span class="spec_label">نوع المركبة</span>
              <h4 class="spec_value">
                {{ formattedVehicleType }}
              </h4>
            </div>
          </div>
        </div>
      </div>

      <!-- DOCUMENTS & PICTURES SECTION -->
      <div class="documents_section">
        <h6 class="docs_section_title mb-3">
          <i class="fal fa-file-certificate me-2"></i>
          الوثائق والمستندات المرفقة
        </h6>

        <div class="row g-4">
          <!-- LICENSE PICTURE -->
          <div class="col-lg-4 col-md-6 col-12 mb-4">
            <div class="doc_image_card">
              <div class="doc_card_header">
                <i class="fal fa-id-badge"></i>
                <span>صورة رخصة القيادة</span>
              </div>
              <div class="doc_image_wrapper">
                <a
                  :href="vehicle.license_picture"
                  target="_blank"
                  v-if="vehicle.license_picture"
                  class="zoom_preview_link"
                >
                  <img
                    :src="vehicle.license_picture"
                    alt="صورة رخصة القيادة"
                    class="doc_img"
                  />
                  <div class="zoom_overlay">
                    <i class="fal fa-search-plus"></i>
                    <span>تكبير الصورة</span>
                  </div>
                </a>
                <div class="no_doc_placeholder" v-else>
                  <i class="fal fa-image-slash"></i>
                  <span>لا توجد صورة مرفقة</span>
                </div>
              </div>
            </div>
          </div>

          <!-- ID PICTURE -->
          <div class="col-lg-4 col-md-6 col-12 mb-4">
            <div class="doc_image_card">
              <div class="doc_card_header">
                <i class="fal fa-id-card"></i>
                <span>صورة الإقامة / الهوية</span>
              </div>
              <div class="doc_image_wrapper">
                <a
                  :href="vehicle.id_picture"
                  target="_blank"
                  v-if="vehicle.id_picture"
                  class="zoom_preview_link"
                >
                  <img
                    :src="vehicle.id_picture"
                    alt="صورة الهوية"
                    class="doc_img"
                  />
                  <div class="zoom_overlay">
                    <i class="fal fa-search-plus"></i>
                    <span>تكبير الصورة</span>
                  </div>
                </a>
                <div class="no_doc_placeholder" v-else>
                  <i class="fal fa-image-slash"></i>
                  <span>لا توجد صورة مرفقة</span>
                </div>
              </div>
            </div>
          </div>

          <!-- CAR FORM PICTURE -->
          <div class="col-lg-4 col-md-6 col-12 mb-4">
            <div class="doc_image_card">
              <div class="doc_card_header">
                <i class="fal fa-file-invoice"></i>
                <span>صورة استمارة السيارة</span>
              </div>
              <div class="doc_image_wrapper">
                <a
                  :href="vehicle.car_form_picture"
                  target="_blank"
                  v-if="vehicle.car_form_picture"
                  class="zoom_preview_link"
                >
                  <img
                    :src="vehicle.car_form_picture"
                    alt="صورة الاستمارة"
                    class="doc_img"
                  />
                  <div class="zoom_overlay">
                    <i class="fal fa-search-plus"></i>
                    <span>تكبير الصورة</span>
                  </div>
                </a>
                <div class="no_doc_placeholder" v-else>
                  <i class="fal fa-image-slash"></i>
                  <span>لا توجد صورة مرفقة</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ["userInfo"],

  computed: {
    vehicle() {
      return this.userInfo && this.userInfo.vehicle
        ? this.userInfo.vehicle
        : null;
    },
    hasVehicleData() {
      if (!this.vehicle) return false;
      return !!(
        this.vehicle.vehicle_number ||
        this.vehicle.vehicle_serial_number ||
        this.vehicle.vehicle_type ||
        this.vehicle.license_picture ||
        this.vehicle.id_picture ||
        this.vehicle.car_form_picture
      );
    },
    formattedVehicleType() {
      if (!this.vehicle || !this.vehicle.vehicle_type) return "غير محدد";
      const types = {
        private: "خاص",
        public: "عام",
        taxi: "تاكسي",
        motor: "دراجة نارية",
      };
      return types[this.vehicle.vehicle_type] || this.vehicle.vehicle_type;
    },
  },
};
</script>

<style lang="scss" scoped>
.vehicle_data_page {
  /* EMPTY STATE */
  .empty_vehicle_card {
    background: #ffffff;
    border-radius: 24px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.04);
    padding: 60px 20px;

    .empty_icon_wrapper {
      position: relative;
      width: 90px;
      height: 90px;

      .empty_icon_box {
        width: 90px;
        height: 90px;
        border-radius: 26px;
        background: linear-gradient(
          135deg,
          rgba(120, 61, 115, 0.1) 0%,
          rgba(120, 61, 115, 0.04) 100%
        );
        border: 2px dashed rgba(120, 61, 115, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 2.4rem;
        color: var(--mainColor-2, #783d73);
        position: relative;
        z-index: 2;
      }

      .pulse_ring {
        position: absolute;
        inset: -8px;
        border-radius: 30px;
        background: rgba(120, 61, 115, 0.08);
        z-index: 1;
        animation: pulseRing 2.5s infinite ease-in-out;
      }
    }

    .empty_title {
      font-size: 1.25rem;
      color: #1e293b;
    }

    .empty_subtitle {
      font-size: 0.9rem;
      line-height: 1.6;
    }
  }

  /* SECTION TITLE BAR */
  .section_title_bar {
    background: #ffffff;
    border-radius: 20px;
    padding: 18px 24px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);

    .title_group {
      display: flex;
      align-items: center;
      gap: 14px;

      .icon_main {
        font-size: 1.5rem;
        color: var(--mainColor-2, #783d73);
      }

      .text_small {
        font-size: 0.825rem;
      }
    }

    .badge_type_pill {
      font-size: 0.85rem;
      font-weight: 700;
      padding: 6px 16px;
      border-radius: 50px;
      background: rgba(120, 61, 115, 0.1);
      color: var(--mainColor-2, #783d73);
    }
  }

  /* SPECIFICATION CARDS */
  .spec_info_card {
    background: #ffffff;
    border-radius: 20px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    padding: 22px 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    box-shadow: 0 8px 20px -5px rgba(15, 23, 42, 0.03);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 15px 30px -5px rgba(15, 23, 42, 0.08);
      border-color: rgba(120, 61, 115, 0.3);
    }

    .spec_icon_box {
      width: 60px;
      height: 60px;
      border-radius: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.6rem;
      flex-shrink: 0;

      &.icon_cyan {
        background: rgba(2, 132, 199, 0.1);
        color: #0284c7;
      }

      &.icon_purple {
        background: rgba(124, 58, 237, 0.1);
        color: #7c3aed;
      }

      &.icon_amber {
        background: rgba(217, 119, 6, 0.1);
        color: #d97706;
      }
    }

    .spec_details {
      .spec_label {
        font-size: 0.8rem;
        color: #64748b;
        font-weight: 500;
        display: block;
        margin-bottom: 4px;
      }

      .spec_value {
        font-size: 1.15rem;
        font-weight: 800;
        color: #1e293b;
        margin: 0;
      }
    }
  }

  /* DOCUMENTS SECTION */
  .documents_section {
    .docs_section_title {
      font-weight: 700;
      color: #334155;
      font-size: 1.05rem;
    }

    .doc_image_card {
      background: #ffffff;
      border-radius: 20px;
      border: 1px solid rgba(226, 232, 240, 0.9);
      overflow: hidden;
      box-shadow: 0 8px 20px -5px rgba(15, 23, 42, 0.04);
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 15px 30px -5px rgba(15, 23, 42, 0.08);
      }

      .doc_card_header {
        padding: 14px 18px;
        background: #f8fafc;
        border-bottom: 1px solid #f1f5f9;
        font-weight: 700;
        font-size: 0.9rem;
        color: #334155;
        display: flex;
        align-items: center;
        gap: 10px;

        i {
          color: var(--mainColor-2, #783d73);
          font-size: 1.05rem;
        }
      }

      .doc_image_wrapper {
        position: relative;
        height: 200px;
        background: #f1f5f9;
        display: flex;
        align-items: center;
        justify-content: center;

        .zoom_preview_link {
          display: block;
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;

          .doc_img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.4s ease;
          }

          .zoom_overlay {
            position: absolute;
            inset: 0;
            background: rgba(15, 23, 42, 0.6);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 6px;
            color: #ffffff;
            opacity: 0;
            transition: opacity 0.3s ease;
            font-size: 0.875rem;
            font-weight: 600;

            i {
              font-size: 1.5rem;
            }
          }

          &:hover {
            .doc_img {
              transform: scale(1.06);
            }

            .zoom_overlay {
              opacity: 1;
            }
          }
        }

        .no_doc_placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: #94a3b8;
          font-size: 0.85rem;
          font-weight: 500;

          i {
            font-size: 2rem;
          }
        }
      }
    }
  }
}

@keyframes pulseRing {
  0% {
    transform: scale(0.95);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.2;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.6;
  }
}
</style>
